import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import config from 'config';
import Permissions from './permissions';
import { observable } from '@legendapp/state';
import { useSelector } from '@legendapp/state/react';

export const UserPermissions = Permissions.map(({ value }) => value);
export type UserPermission = (typeof UserPermissions)[number];

export interface IUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userName: string;
  fullName?: string;
  phoneNumber?: string;
  avatar?: string;
  profilePicture?: string;
  roleId?: number;
  mustChangePassword?: boolean;
  passwordChangedAt?: string | Date;
  lastLogin?: Date | string;
  isActive?: boolean;
  isEmailVerified?: boolean;
  twoFAEnabled?: boolean;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  role?: {
    id?: string;
    name?: string;
    description?: string;
    permissions?: UserPermission[];
  };
  meta?: {
    lastLoginAt?: Date;
  };
}

interface AuthState {
  token: string | null;
  user: IUser | null;
  pushToken: string | null;
  isLoggedIn: boolean;
}

const getInitialUser = (): IUser | null => {
  try {
    return JSON.parse(Cookies.get(`application:auth:user`) ?? 'null');
  } catch {
    return null;
  }
};

const auth$ = observable<AuthState>({
  token: Cookies.get(`application:auth:token`) || null,
  user: getInitialUser(),
  pushToken: Cookies.get(`application:auth:push-token`) || null,
  isLoggedIn: !!Cookies.get(`application:auth:token`),
});

export const authState = () => auth$.get();

export const useAuthStore = <T>(selector: (state: AuthState) => T) => useSelector(() => selector(auth$.get()));

export const setAuth = ({ user, token }: { user: IUser; token: string }) => {
  Cookies.set(`application:auth:user`, JSON.stringify(user), {
    ...config.cookies,
    expires: 1,
  });
  Cookies.set(`application:auth:token`, token, {
    ...config.cookies,
    expires: 1,
  });
  auth$.set({
    ...auth$.get(),
    token,
    user,
    isLoggedIn: true,
  });
};

export const setMe = (user: IUser) => {
  Cookies.set(`application:auth:user`, JSON.stringify(user), {
    ...config.cookies,
    expires: 1,
  });
  auth$.set({ ...auth$.get(), user });
};

export const clearAuth = () => {
  Object.keys(Cookies.get()).forEach(key => {
    Cookies.remove(key, { ...config.cookies });
  });
  auth$.set({
    token: null,
    user: null,
    pushToken: null,
    isLoggedIn: false,
  });
};

export const initiateClearAuth = () => {
  toast(
    JSON.stringify({
      type: 'logout',
      title: 'Are you sure you want to log out?',
    }),
    { duration: 1000 * 10, id: 'logout' },
  );
};
